import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Skeleton,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SyncIcon from '@mui/icons-material/Sync';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useLocation, useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import AppLayout from '../layout/AppLayout';
import TemplatePreview, { oneLine } from './TemplatePreview';
import { TemplatePerformancePanel, TemplatePerformanceReport } from './TemplatePerformance';
import { whatsappAccountService } from '../../services/api';
import { INK, MUTED, RULE, formatCount } from '../analytics/analyticsUi';

const WHATSAPP_MANAGER_URL = 'https://business.facebook.com/wa/manage/message-templates/';
const LAST_ACCOUNT_KEY = 'templates_last_account';

const STATUS_META = {
  APPROVED: { label: 'Approved', color: '#1BAA52' },
  PENDING: { label: 'In review', color: '#C77700' },
  IN_APPEAL: { label: 'In appeal', color: '#C77700' },
  REJECTED: { label: 'Rejected', color: '#C2410C' },
  PAUSED: { label: 'Paused', color: '#C2410C' },
  DISABLED: { label: 'Disabled', color: '#C2410C' },
  LIMIT_EXCEEDED: { label: 'Limit exceeded', color: '#C2410C' },
  PENDING_DELETION: { label: 'Being deleted', color: MUTED },
  DELETED: { label: 'Deleted', color: MUTED },
  ARCHIVED: { label: 'Archived', color: MUTED }
};

const CATEGORY_LABELS = {
  MARKETING: 'Marketing',
  UTILITY: 'Utility',
  AUTHENTICATION: 'Authentication'
};

const QUALITY_META = {
  GREEN: { label: 'High quality', color: '#1BAA52' },
  YELLOW: { label: 'Medium quality', color: '#C77700' },
  RED: { label: 'Low quality', color: '#C2410C' }
};

const statusMeta = (status) => STATUS_META[status] || { label: status || 'Unknown', color: MUTED };
const categoryLabel = (category) => CATEGORY_LABELS[category] || category || 'Uncategorised';
const readableName = (name = '') => name.replace(/_/g, ' ');

const languageName = (code) => {
  try {
    const display = new Intl.DisplayNames(['en'], { type: 'language' });
    return display.of(code.replace('_', '-')) || code;
  } catch (err) {
    return code;
  }
};

const minutesAgo = (iso) => {
  if (!iso) return '';
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return 'just now';
  return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;
};

const Dot = ({ color }) => (
  <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, display: 'inline-block', flexShrink: 0 }} />
);

// ---------------------------------------------------------------------------
// Detail panel for the selected template
// ---------------------------------------------------------------------------
const DetailRow = ({ label, children }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 2, py: 1, borderTop: `1px solid ${RULE}` }}>
    <Typography variant="body2" sx={{ color: MUTED }}>{label}</Typography>
    <Box sx={{ color: INK, fontSize: '0.875rem', minWidth: 0, overflowWrap: 'anywhere' }}>{children}</Box>
  </Box>
);

const TemplateDetail = ({ template, accountId, onDelete, onDuplicate, onClose }) => {
  const status = statusMeta(template.status);
  const quality = QUALITY_META[template.quality_score?.score];
  const buttons = (template.components || []).find((c) => c.type === 'BUTTONS')?.buttons || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h2" sx={{ fontSize: '1.25rem', fontWeight: 700, color: INK, overflowWrap: 'anywhere' }}>
            {template.name}
          </Typography>
          <Typography variant="body2" sx={{ color: MUTED, mt: 0.25 }}>
            {languageName(template.language)}, {categoryLabel(template.category)}
          </Typography>
        </Box>
        {onClose && (
          <IconButton onClick={onClose} aria-label="Close template details" size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {template.status === 'REJECTED' && template.rejected_reason && template.rejected_reason !== 'NONE' && (
        <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
          Meta rejected this template: {readableName(template.rejected_reason.toLowerCase())}.
        </Alert>
      )}

      <TemplatePreview template={template} />

      <Box sx={{ mt: 2.5 }}>
        <DetailRow label="Status">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Dot color={status.color} />
            {status.label}
          </Box>
        </DetailRow>
        <DetailRow label="Quality">
          {quality ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Dot color={quality.color} />
              {quality.label}
            </Box>
          ) : 'Not rated yet'}
        </DetailRow>
        {template.previous_category && template.previous_category !== template.category && (
          <DetailRow label="Category change">
            Meta moved this template from {categoryLabel(template.previous_category)} to {categoryLabel(template.category)}.
          </DetailRow>
        )}
        {buttons.filter((b) => b.url || b.phone_number).map((button, index) => (
          <DetailRow key={index} label={button.type === 'PHONE_NUMBER' ? 'Calls' : 'Opens'}>
            {button.url || button.phone_number}
          </DetailRow>
        ))}
        <DetailRow label="Template ID">
          <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>{template.id}</Box>
        </DetailRow>
      </Box>

      <Box sx={{ mt: 3, pt: 2.5, borderTop: `1px solid ${RULE}` }}>
        <TemplatePerformancePanel key={template.id} accountId={accountId} template={template} />
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 3 }}>
        <Button variant="outlined" onClick={() => onDuplicate(template)} sx={{ textTransform: 'none' }}>
          Duplicate
        </Button>
        <Button
          variant="outlined"
          color="error"
          onClick={() => onDelete(template)}
          disabled={['PENDING_DELETION', 'DELETED'].includes(template.status)}
          sx={{ textTransform: 'none' }}
        >
          Delete template
        </Button>
        <Button
          component="a"
          href={WHATSAPP_MANAGER_URL}
          target="_blank"
          rel="noopener noreferrer"
          endIcon={<OpenInNewIcon fontSize="small" />}
          sx={{ textTransform: 'none' }}
        >
          Open in WhatsApp Manager
        </Button>
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const Templates = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isWide = useMediaQuery(theme.breakpoints.up('lg'));

  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [accountId, setAccountId] = useState('');

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(() => location.state?.notice || null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [view, setView] = useState('templates'); // templates | report

  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteScope, setDeleteScope] = useState('language');
  const [deleting, setDeleting] = useState(false);

  // Accounts
  useEffect(() => {
    const load = async () => {
      try {
        const response = await whatsappAccountService.getAccounts(1, 1000);
        const list = (response.data || []).filter((a) => a.wabaId);
        setAccounts(list);
        let saved = null;
        try { saved = localStorage.getItem(LAST_ACCOUNT_KEY); } catch (err) { /* storage unavailable */ }
        const fromBuilder = location.state?.accountId;
        setAccountId(list.find((a) => a._id === fromBuilder)?._id || list.find((a) => a._id === saved)?._id || list[0]?._id || '');
      } catch (err) {
        setError('Your WhatsApp accounts could not be loaded.');
      } finally {
        setAccountsLoading(false);
      }
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- only the account passed in on arrival matters

  const sync = useCallback(async () => {
    if (!accountId) return;
    try {
      setLoading(true);
      setError(null);
      const response = await whatsappAccountService.getMetaTemplates(accountId);
      setResult(response.data);
    } catch (err) {
      setResult(null);
      setError(err.response?.data?.error || 'Templates could not be loaded from Meta. Try again.');
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    setSelectedId(null);
    sync();
    try { if (accountId) localStorage.setItem(LAST_ACCOUNT_KEY, accountId); } catch (err) { /* storage unavailable */ }
  }, [accountId, sync]);

  const templates = useMemo(() => result?.templates || [], [result]);

  const statusCounts = useMemo(() => {
    const counts = {};
    templates.forEach((t) => { counts[t.status] = (counts[t.status] || 0) + 1; });
    return counts;
  }, [templates]);

  const categories = useMemo(() => [...new Set(templates.map((t) => t.category).filter(Boolean))], [templates]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return templates
      .filter((t) => !statusFilter || t.status === statusFilter)
      .filter((t) => !categoryFilter || t.category === categoryFilter)
      .filter((t) => !query || t.name.toLowerCase().includes(query) ||
        (t.components || []).some((c) => oneLine(c.text || '').toLowerCase().includes(query)))
      .sort((a, b) => a.name.localeCompare(b.name) || a.language.localeCompare(b.language));
  }, [templates, search, statusFilter, categoryFilter]);

  const selected = templates.find((t) => t.id === selectedId) || null;
  const siblingLanguages = pendingDelete ? templates.filter((t) => t.name === pendingDelete.name) : [];

  const duplicate = (template) => navigate('/templates/new', { state: { accountId, template } });

  const openDelete = (template) => {
    setDeleteScope('language');
    setPendingDelete(template);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const allLanguages = deleteScope === 'all' || siblingLanguages.length <= 1;
    try {
      setDeleting(true);
      await whatsappAccountService.deleteMetaTemplate(accountId, {
        name: pendingDelete.name,
        hsmId: allLanguages ? undefined : pendingDelete.id
      });
      setResult((prev) => prev && {
        ...prev,
        templates: prev.templates.filter((t) => (allLanguages ? t.name !== pendingDelete.name : t.id !== pendingDelete.id))
      });
      setNotice(`${pendingDelete.name}${allLanguages ? '' : ` (${languageName(pendingDelete.language)})`} deleted in Meta`);
      setSelectedId(null);
      setPendingDelete(null);
    } catch (err) {
      setError(err.response?.data?.error || 'The template could not be deleted. Try again.');
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const hasFilters = !!(search || statusFilter || categoryFilter);
  const currentAccount = accounts.find((a) => a._id === accountId);

  // ---- Rendering helpers
  const listPanel = (
    <Paper variant="outlined" sx={{ borderColor: RULE, borderRadius: 2, overflow: 'hidden' }}>
      {loading && !result ? (
        <Box sx={{ p: 3 }} aria-busy="true">
          {[0, 1, 2, 3, 4].map((i) => (
            <Box key={i} sx={{ py: 1.5, borderTop: i ? `1px solid ${RULE}` : 'none' }}>
              <Skeleton width="45%" /><Skeleton width="30%" />
            </Box>
          ))}
        </Box>
      ) : !filtered.length ? (
        <Box sx={{ p: { xs: 3, sm: 5 } }}>
          {hasFilters ? (
            <>
              <Typography sx={{ fontWeight: 600, color: INK, mb: 1 }}>No templates match these filters</Typography>
              <Button
                variant="outlined"
                onClick={() => { setSearch(''); setStatusFilter(''); setCategoryFilter(''); }}
                sx={{ textTransform: 'none', mt: 1 }}
              >
                Clear filters
              </Button>
            </>
          ) : (
            <>
              <Typography sx={{ fontWeight: 600, color: INK, mb: 1 }}>This account has no templates in Meta</Typography>
              <Typography variant="body2" sx={{ color: MUTED, maxWidth: '60ch', mb: 2.5 }}>
                Templates are needed to start conversations with customers. Create one in WhatsApp Manager, then sync this page.
              </Typography>
              <Button
                variant="outlined"
                component="a"
                href={WHATSAPP_MANAGER_URL}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<OpenInNewIcon fontSize="small" />}
                sx={{ textTransform: 'none' }}
              >
                Open WhatsApp Manager
              </Button>
            </>
          )}
        </Box>
      ) : (
        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, opacity: loading ? 0.55 : 1, transition: 'opacity 150ms' }}>
          {filtered.map((template, index) => {
            const status = statusMeta(template.status);
            const body = oneLine((template.components || []).find((c) => c.type === 'BODY')?.text || '');
            const isSelected = template.id === selectedId;
            return (
              <Box component="li" key={template.id} sx={{ borderTop: index ? `1px solid ${RULE}` : 'none' }}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => setSelectedId(isSelected ? null : template.id)}
                  aria-expanded={isSelected}
                  sx={{
                    all: 'unset',
                    boxSizing: 'border-box',
                    width: '100%',
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr auto', sm: 'minmax(0, 1fr) 140px 130px' },
                    columnGap: 2,
                    alignItems: 'center',
                    px: { xs: 2, sm: 3 },
                    py: 1.5,
                    cursor: 'pointer',
                    bgcolor: isSelected ? 'rgba(37, 211, 102, 0.07)' : 'transparent',
                    boxShadow: isSelected ? `inset 3px 0 0 ${theme.palette.primary.main}` : 'none',
                    '&:hover': { bgcolor: isSelected ? 'rgba(37, 211, 102, 0.1)' : 'rgba(31, 42, 61, 0.03)' },
                    '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 }
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ color: INK, fontWeight: 600 }} noWrap>{template.name}</Typography>
                    <Typography variant="body2" sx={{ color: MUTED }} noWrap>
                      {languageName(template.language)}
                      {body && <Box component="span" sx={{ ml: 1.5 }}>{body}</Box>}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: INK, display: { xs: 'none', sm: 'block' } }}>
                    {categoryLabel(template.category)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifySelf: { xs: 'end', sm: 'start' } }}>
                    <Dot color={status.color} />
                    <Typography variant="body2" sx={{ color: INK, whiteSpace: 'nowrap' }}>{status.label}</Typography>
                  </Box>
                </Box>
                {!isWide && isSelected && (
                  <Box sx={{ px: { xs: 2, sm: 3 }, pb: 3, pt: 1 }}>
                    <TemplateDetail template={template} accountId={accountId} onDelete={openDelete} onDuplicate={duplicate} />
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Paper>
  );

  return (
    <AppLayout>
      <Container maxWidth="lg" sx={{ pb: 6 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2, mb: 3 }}>
          <Box>
            <Typography
              component="h1"
              sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' }, fontWeight: 700, letterSpacing: '-0.025em', color: INK, lineHeight: 1.15 }}
            >
              Message templates
            </Typography>
            <Typography variant="body2" sx={{ color: MUTED, mt: 0.75 }}>
              {result && !loading
                ? `${formatCount(result.count)} ${result.count === 1 ? 'template' : 'templates'} in Meta, synced ${minutesAgo(result.syncedAt)}`
                : 'Approved templates are the only way to message customers outside the 24-hour window.'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Autocomplete
              size="small"
              sx={{ width: { xs: '100%', sm: 320 } }}
              options={accounts}
              value={accounts.find((a) => a._id === accountId) || null}
              onChange={(e, account) => account && setAccountId(account._id)}
              disableClearable
              loading={accountsLoading}
              disabled={accountsLoading || !accounts.length}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              getOptionLabel={(account) => `${account.verifiedName || account.businessName}, ${account.phoneNumber}`}
              // Match name, phone number (with or without spaces) and, for admins, the customer
              filterOptions={(options, { inputValue }) => {
                const query = inputValue.trim().toLowerCase();
                if (!query) return options;
                const digits = query.replace(/\D/g, '');
                return options.filter((a) => {
                  const text = [a.verifiedName, a.businessName, a.user?.company, a.user?.name, a.user?.email]
                    .filter(Boolean).join(' ').toLowerCase();
                  const phone = String(a.phoneNumber || '').replace(/\D/g, '');
                  return text.includes(query) || (digits.length >= 3 && phone.includes(digits));
                });
              }}
              noOptionsText="No accounts match"
              renderOption={(props, account) => (
                <li {...props} key={account._id}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ color: INK }} noWrap>{account.verifiedName || account.businessName}</Typography>
                    <Typography variant="caption" sx={{ color: MUTED, display: 'block' }} noWrap>
                      {account.phoneNumber}
                      {account.user?.company || account.user?.name ? `, ${account.user.company || account.user.name}` : ''}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="WhatsApp account" placeholder="Search name or number" />
              )}
            />
            <Button
              variant="outlined"
              onClick={sync}
              disabled={!accountId || loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {loading ? 'Syncing…' : 'Sync from Meta'}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/templates/new', { state: { accountId } })}
              disabled={!accounts.length}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Create template
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {!accountsLoading && !accounts.length ? (
          <Paper variant="outlined" sx={{ p: { xs: 3, sm: 5 }, borderColor: RULE, borderRadius: 2 }}>
            <Typography sx={{ fontWeight: 600, color: INK, mb: 1 }}>Connect a WhatsApp account first</Typography>
            <Typography variant="body2" sx={{ color: MUTED, mb: 2.5, maxWidth: '60ch' }}>
              Templates belong to a WhatsApp Business account. Connect one to see and manage its templates here.
            </Typography>
            <Button variant="contained" component={RouterLink} to="/whatsapp-onboarding" sx={{ textTransform: 'none' }}>
              Connect WhatsApp Business
            </Button>
          </Paper>
        ) : (
          <>
            {templates.length > 0 && (
              <ToggleButtonGroup
                size="small"
                exclusive
                value={view}
                onChange={(e, v) => v && setView(v)}
                aria-label="Page view"
                sx={{ mb: 2 }}
              >
                <ToggleButton value="templates" sx={{ textTransform: 'none', px: 2 }}>Templates</ToggleButton>
                <ToggleButton value="report" sx={{ textTransform: 'none', px: 2 }}>Performance report</ToggleButton>
              </ToggleButtonGroup>
            )}

            {view === 'report' && templates.length > 0 ? (
              <TemplatePerformanceReport
                accountId={accountId}
                accountName={currentAccount?.verifiedName || currentAccount?.businessName}
                templates={templates}
                categoryLabel={categoryLabel}
                languageName={languageName}
                onOpen={(template) => { setSelectedId(template.id); setView('templates'); }}
              />
            ) : (
            <>
            {/* Filters */}
            {templates.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: { xs: 1.5, md: 2 }, mb: 2 }}>
                <TextField
                  size="small"
                  placeholder="Search name or message text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  inputProps={{ 'aria-label': 'Search templates' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: MUTED }} />
                      </InputAdornment>
                    )
                  }}
                  sx={{ flex: { xs: '1 1 100%', md: '1 1 280px' }, maxWidth: { md: 380 }, bgcolor: 'background.paper' }}
                />
                <FormControl size="small" sx={{ minWidth: 170 }}>
                  <InputLabel id="template-status-label">Status</InputLabel>
                  <Select labelId="template-status-label" label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <MenuItem value="">Any status</MenuItem>
                    {Object.entries(statusCounts).map(([status, count]) => (
                      <MenuItem key={status} value={status}>{statusMeta(status).label} ({count})</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 170 }}>
                  <InputLabel id="template-category-label">Category</InputLabel>
                  <Select labelId="template-category-label" label="Category" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                    <MenuItem value="">Any category</MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category} value={category}>{categoryLabel(category)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {hasFilters && (
                  <Typography variant="body2" sx={{ color: MUTED }}>
                    {formatCount(filtered.length)} of {formatCount(templates.length)}
                  </Typography>
                )}
              </Box>
            )}

            {isWide ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 7fr) minmax(360px, 5fr)', gap: 3, alignItems: 'start' }}>
                {listPanel}
                <Paper
                  variant="outlined"
                  sx={{ borderColor: RULE, borderRadius: 2, p: 3, position: 'sticky', top: 88 }}
                >
                  {selected ? (
                    <TemplateDetail template={selected} accountId={accountId} onDelete={openDelete} onDuplicate={duplicate} onClose={() => setSelectedId(null)} />
                  ) : (
                    <Box sx={{ py: 4 }}>
                      <Typography sx={{ fontWeight: 600, color: INK, mb: 0.75 }}>Select a template</Typography>
                      <Typography variant="body2" sx={{ color: MUTED }}>
                        See how it looks in WhatsApp, its review status, quality rating and performance.
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Box>
            ) : listPanel}
            </>
            )}
          </>
        )}

        {/* Delete confirmation */}
        <Dialog open={!!pendingDelete} onClose={() => !deleting && setPendingDelete(null)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ color: INK, fontWeight: 600, overflowWrap: 'anywhere' }}>
            Delete {pendingDelete?.name}?
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: MUTED, mb: siblingLanguages.length > 1 ? 2 : 0 }}>
              The template is deleted in Meta for {currentAccount?.verifiedName || currentAccount?.businessName}. Messages using it
              stop sending, and the name can't be reused for 30 days.
            </Typography>
            {siblingLanguages.length > 1 && (
              <RadioGroup value={deleteScope} onChange={(e) => setDeleteScope(e.target.value)}>
                <FormControlLabel
                  value="language"
                  control={<Radio size="small" />}
                  label={`Only the ${languageName(pendingDelete?.language)} version`}
                />
                <FormControlLabel
                  value="all"
                  control={<Radio size="small" />}
                  label={`All ${siblingLanguages.length} languages`}
                />
              </RadioGroup>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setPendingDelete(null)} disabled={deleting} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button
              onClick={confirmDelete}
              variant="contained"
              color="error"
              disabled={deleting}
              startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ textTransform: 'none' }}
            >
              {deleting ? 'Deleting…' : 'Delete in Meta'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={!!notice} autoHideDuration={4000} onClose={() => setNotice(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert onClose={() => setNotice(null)} severity="success" variant="filled" sx={{ width: '100%' }}>
            {notice}
          </Alert>
        </Snackbar>
      </Container>
    </AppLayout>
  );
};

export default Templates;
